import { gsap } from "gsap";

/**
 * Class to manage multiple GSAP timelines.
 */
class TimelineManager {
    public timelines: gsap.core.Timeline[] = [];

    /**
     * Creates a new GSAP timeline and adds it to the timelines array.
     * @param {gsap.TimelineVars} vars - The configuration object for the timeline.
     * @returns {gsap.core.Timeline} The created timeline.
     */
    public createTimeline(vars?: gsap.TimelineVars): gsap.core.Timeline {
        const timeline = gsap.timeline({
            ...vars,
            onComplete: () => {
                const index = this.timelines.indexOf(timeline);
                if (index > -1) {
                    this.timelines.splice(index, 1);
                }
                vars?.onComplete?.();
            },
        });
        this.timelines.push(timeline);
        timeline.play();
        return timeline;
    }

    /**
     * Creates a new tween and adds it to a new timeline.
     * @param {object} target - The target of the tween.
     * @param {gsap.TweenVars} vars - The configuration object for the tween.
     * @returns {gsap.core.Tween} The created tween.
     */
    public to(target: object, vars: gsap.TweenVars): gsap.core.Tween {
        const timeline = this.createTimeline();
        const tween = gsap.to(target, vars);
        timeline.add(tween);
        return tween;
    }

    /**
     * Kills tweens of the given targets.
     * @param {gsap.TweenTarget} targets - The targets of the tweens to kill.
     * @param {string | object} properties - The properties of the tweens to kill.
     * @param {boolean} onlyActive - Whether to only kill active tweens.
     */
    public killTweensOf(targets: gsap.TweenTarget, properties?: string | object, onlyActive?: boolean): void {
        gsap.killTweensOf(targets, properties, onlyActive);
    }

    /**
     * Adds an animation to a timeline.
     * @param {gsap.core.Animation} animation - The animation to add.
     * @param {gsap.core.Timeline} timeline - The timeline to add the animation to.
     */
    public addToTimeline(animation: gsap.core.Animation, timeline: gsap.core.Timeline): void {
        timeline.add(animation);
    }

    /**
     * Pauses a timeline.
     * @param {gsap.core.Timeline} timeline - The timeline to pause.
     */
    public pauseTimeline(timeline: gsap.core.Timeline): void {
        timeline.pause();
    }

    /**
     * Pauses all timelines.
     */
    public pauseAllTimelines(): void {
        gsap.globalTimeline.pause();
    }

    /**
     * Resumes all timelines.
     */
    public resumeAllTimelines(): void {
        gsap.globalTimeline.resume();
    }

    /**
     * Resets a timeline and removes it from the timelines array.
     * @param {gsap.core.Timeline} timeline - The timeline to reset.
     */
    public resetTimeline(timeline: gsap.core.Timeline): void {
        const index = this.timelines.indexOf(timeline);
        if (index > -1) {
            timeline.clear();
            this.timelines.splice(index, 1);
        }
    }
}

export default new TimelineManager();
